import { Suspense, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useOverdueFollowUps } from '../features/activities/useOverdueFollowUps';
import { MailboxConnectBanner } from '../features/email/connect/MailboxConnectBanner';
import { Button } from '../ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/shadcn/tooltip';
import styles from './AppLayout.module.css';
import { ComposeEmailProvider } from './ComposeEmailProvider';
import { CurrentUserMenu } from './CurrentUserMenu';
import type { NavBadge } from './navConfig';
import { focusPageHeading, PageHeadingFocus } from './PageHeadingFocus';
import { RouteSkeleton } from './RouteSkeleton';
import { RouteTheme } from './RouteTheme';
import { RouteTitle } from './RouteTitle';
import { renderGatedNavItem } from './renderGatedNavItem';
import { Sidebar, type SidebarMode } from './Sidebar';
import { sidebarPreference } from './sidebarPreference';
import { useSidebarLayout } from './useSidebarLayout';

/** The sidebar toggle's names, shown in its tooltip too (AD #39). */
const COLLAPSE_SIDEBAR = 'Collapse sidebar';
const EXPAND_SIDEBAR = 'Expand sidebar';

/**
 * The *Activities* entry's alert badge: the signed-in user's overdue follow-ups, none at zero (F3-a spec
 * FR-V7, AC-23; F3-a WI-2.2 plan Decision 21).
 */
function activitiesBadge(overdue: number | null): Readonly<Record<string, NavBadge>> | undefined {
    if (overdue === null || overdue <= 0) {
        return undefined;
    }
    return { activities: { kind: 'alert', value: overdue, label: `${overdue} overdue` } };
}

/**
 * The §6 app frame every authenticated route renders inside: the collapsible `Sidebar` and a scrolling
 * content canvas that keeps the single Suspense boundary around the routed `<Outlet/>`. This replaces
 * F5-a's interim top-header shell (F5-b FR-1). There is no top bar on any route (AD #39, the UI/UX
 * review): the sidebar toggle is in the sidebar header, and the frame has no global-search control —
 * search across all records is out of V1 (F31 dropped, PRD Rev 60), and each list keeps its own search.
 *
 * One Suspense boundary here rather than one per route: a lazily-loaded element with no Suspense
 * ancestor throws at render, so putting it on the layout means a new route cannot forget it, and the
 * fallback replaces only the canvas so the frame stays on screen while a chunk loads.
 *
 * `AppLayout` owns the layout state (tier, collapse preference, overlay-open) because a route
 * element takes no props; it is therefore the single file F5-b WI-1.2 edits on its stacked branch to
 * wire the sidebar `footer` (current-user menu) and the `renderNavItem` gating seam (F5-b WI-1.1 plan Decision 5).
 */
export function AppLayout() {
    const tier = useSidebarLayout();
    const badges = activitiesBadge(useOverdueFollowUps());
    const [collapsed, setCollapsed] = useState(() => sidebarPreference.read());
    const [overlayOpen, setOverlayOpen] = useState(false);
    const sidebarId = useId();
    const toggleId = useId();
    // Where focus goes once a close has committed. `always`: a close a person made with Esc or the
    // scrim returns focus to the toggle. `if-lost`: a resize away from the tablet tier closes the
    // drawer too, but moves focus only when the focused element did not survive (the scrim, which a
    // pointer press can focus although Tab cannot reach it).
    const restoreToggleFocus = useRef<'none' | 'always' | 'if-lost'>('none');
    // A nav choice in the drawer sends focus to the new page's main heading instead, once that page
    // has rendered — `PageHeadingFocus`, inside the Suspense boundary, consumes this.
    const focusPageOnNavigate = useRef(false);
    const mainRef = useRef<HTMLElement>(null);

    // Revamp (AD #39): the sidebar toggle is in the sidebar header, so below bp/laptop the rail stays on
    // screen icon-only to hold it, and the toggle opens the full drawer over the page.

    // §9 precedence (F5-b WI-1.1 plan Decision 6): the responsive tier wins; the manual collapse preference applies
    // only at bp/desktop. tablet → overlay, laptop → forced icon-only, desktop → pref decides.
    // At tablet, the icon rail while the drawer is closed and the overlay while open.
    const mode: SidebarMode =
        tier === 'tablet'
            ? !overlayOpen
                ? 'icon'
                : 'overlay'
            : tier === 'laptop'
              ? 'icon'
              : collapsed
                ? 'icon'
                : 'expanded';
    const drawerOpen = mode === 'overlay' && overlayOpen;
    // The toggle opens the drawer at tablet and collapses the rail at desktop. At laptop the rail is
    // forced icon-only, so the toggle would do nothing visible: it is hidden there (display: none).
    const toggleShown = tier !== 'laptop';

    // A resize away from the tablet tier must not strand the overlay open (plan Risk).
    useEffect(() => {
        if (tier !== 'tablet' && overlayOpen) {
            if (restoreToggleFocus.current === 'none') {
                restoreToggleFocus.current = 'if-lost';
            }
            setOverlayOpen(false);
        }
    }, [tier, overlayOpen]);

    // Focus goes back to the toggle AFTER the close has committed: until then the drawer is still
    // closing, and the toggle it returns to is in the rail it reopens as.
    useEffect(() => {
        if (overlayOpen || restoreToggleFocus.current === 'none') {
            return;
        }
        const request = restoreToggleFocus.current;
        restoreToggleFocus.current = 'none';
        const active = document.activeElement;
        const focusLost = active === null || active === document.body || !active.isConnected;
        if (request === 'always' || focusLost) {
            // A resize from tablet to laptop can close the drawer; the toggle is hidden there, so focus
            // goes to the page's main heading instead.
            if (toggleShown) {
                document.getElementById(toggleId)?.focus();
            } else if (mainRef.current !== null) {
                focusPageHeading(mainRef.current);
            }
        }
    }, [overlayOpen, toggleId, toggleShown]);

    // A resize into the laptop tier hides the toggle (display: none). If the toggle had focus, the
    // browser drops focus to the body, so send it to the page's main heading instead. Only on the
    // transition, and only when focus is on the toggle or already lost: anything else keeps it.
    const toggleWasShown = useRef(toggleShown);
    useEffect(() => {
        const wasShown = toggleWasShown.current;
        toggleWasShown.current = toggleShown;
        if (!wasShown || toggleShown || mainRef.current === null) {
            return;
        }
        const active = document.activeElement;
        if (active === null || active === document.body || active === document.getElementById(toggleId)) {
            focusPageHeading(mainRef.current);
        }
    }, [toggleShown, toggleId]);

    // When the connect-failed banner leaves after a press of its own button, that button goes with it and the
    // banner is outside every page's focus handling: focus goes to the page's heading (`.forge/lessons.md` L-133).
    const focusPageHeadingAfterBanner = useCallback(() => {
        if (mainRef.current !== null) {
            focusPageHeading(mainRef.current);
        }
    }, []);

    // Stable across renders, so the Sidebar's Esc listener is not torn down and re-added each render.
    const closeOverlay = useCallback(() => {
        restoreToggleFocus.current = 'always';
        setOverlayOpen(false);
    }, []);

    const closeOverlayOnNavigate = useCallback(() => {
        restoreToggleFocus.current = 'none';
        focusPageOnNavigate.current = true;
        setOverlayOpen(false);
    }, []);

    function handleToggle() {
        if (tier === 'tablet') {
            // The toggle is reachable only while the drawer is closed (the column is inert while it
            // is open), so this is an open: drop any focus return a close left pending, or it would
            // fire on the next close that is not a person's.
            restoreToggleFocus.current = 'none';
            focusPageOnNavigate.current = false;
            setOverlayOpen((open) => !open);
            return;
        }
        // desktop: flip and persist the collapse preference. (The toggle is hidden at laptop, where
        // `mode` is forced icon-only; a preference stored at desktop applies again once back there.)
        // The write stays out of the state updater: an updater must be pure, and React may call it
        // twice (StrictMode) or replay it.
        const next = !collapsed;
        setCollapsed(next);
        sidebarPreference.write(next);
    }

    // F5-b WI-1.2 (C2 / F5-b WI-1.2 plan Decision 4/10): the current-user menu ABSORBS and REMOVES F5-a's interim
    // top-level sign-out — the sidebar footer now hosts `CurrentUserMenu`, whose single Sign out
    // item is the one sign-out control (F5-b FR-6/AC-6). `renderGatedNavItem` wraps each nav entry in
    // the shipped PermissionGuard through F5-a WI-1.1's `renderNavItem` seam (F5-b FR-4/FR-5).
    // Tablet: whether the drawer is open. Above it: whether the rail is expanded — at desktop that is
    // `!collapsed`; at laptop the rail is forced icon-only, so false.
    const expanded = mode === 'overlay' ? drawerOpen : mode === 'expanded';

    return (
        // The compose opener (F20-c FR-O1) wraps the whole frame, so its dialog renders outside the
        // column that turns `inert` while the tablet drawer is open (F20-c WI-1.2 plan D-11).
        <ComposeEmailProvider>
            <div style={{ display: 'flex', minHeight: '100vh', maxHeight: '100vh' }}>
                <Sidebar
                    id={sidebarId}
                    mode={mode}
                    overlayOpen={overlayOpen}
                    onOverlayClose={closeOverlay}
                    onOverlayNavigate={closeOverlayOnNavigate}
                    footer={<CurrentUserMenu collapsed={mode === 'icon'} />}
                    renderNavItem={renderGatedNavItem}
                    badges={badges}
                    // Revamp (AD #39): the toggle in the sidebar header, on every route. Not rendered at laptop,
                    // where the rail is forced icon-only and the toggle would do nothing visible. It is named
                    // for what it does next — Collapse sidebar while the sidebar is expanded, Expand sidebar
                    // while it is the icon rail (the tablet drawer included) — and its tooltip shows the same
                    // words on hover and focus, so an icon-only button says what it does.
                    {...(toggleShown
                        ? {
                              toggle: (
                                  <Tooltip>
                                      <TooltipTrigger asChild describesTrigger={false}>
                                          <Button
                                              variant="ghost-icon"
                                              icon="sidebarToggle"
                                              size="sm"
                                              aria-label={expanded ? COLLAPSE_SIDEBAR : EXPAND_SIDEBAR}
                                              aria-controls={sidebarId}
                                              aria-expanded={expanded}
                                              id={toggleId}
                                              data-testid="app-sidebar-toggle"
                                              onClick={handleToggle}
                                          />
                                      </TooltipTrigger>
                                      <TooltipContent side={mode === 'expanded' ? 'bottom' : 'right'}>
                                          {expanded ? COLLAPSE_SIDEBAR : EXPAND_SIDEBAR}
                                      </TooltipContent>
                                  </Tooltip>
                              ),
                          }
                        : {})}
                />
                {/* Revamp (AD #39): while the drawer is open over the tablet rail, this holds the rail's width, so
                    the page does not shift left under the scrim. */}
                {drawerOpen ? (
                    <div
                        aria-hidden="true"
                        data-testid="app-sidebar-rail-spacer"
                        style={{ flex: 'none', inlineSize: 'var(--et-layout-sidebar-width-collapsed)' }}
                    />
                ) : null}
                {/* While the tablet drawer is open, the column behind it — the canvas — is `inert`, so Tab and
                    assistive tech stay inside the drawer (WCAG 2.4.3); the scrim closes it. */}
                <div
                    style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}
                    inert={drawerOpen ? true : undefined}
                >
                    {/* `styles.main` drops the focus ring on this element and its `h1` when script focuses
                        them (`tabindex="-1"`, `focusPageHeading`); they are reading positions, not controls. */}
                    <main
                        ref={mainRef}
                        className={styles.main}
                        style={{
                            flex: 1,
                            overflow: 'auto',
                            // The containing block for the screens' visually hidden legends and reasons
                            // (`position: absolute`). Without it they are placed against the page, outside this
                            // scroll box, and each one below the fold makes the whole frame scroll (BUG-153).
                            position: 'relative',
                            background: 'var(--et-surface-canvas)',
                            // §9 canvas gutter: space/7 at desktop/laptop, reducing to space/6 at tablet.
                            // Dynamic, so inline is the sanctioned form; both values are tokens (F5-b AC-14).
                            padding: tier === 'tablet' ? 'var(--et-space-6)' : 'var(--et-space-7)',
                        }}
                    >
                        {/* Outside the Suspense boundary, so the theme follows the route at once rather
                            than after the lazy route chunk loads. */}
                        <RouteTheme />
                        {/* The mailbox connect's failure (F20-i FR-17), full width at the top of every page and
                            outside the Suspense boundary, so it shows while a route chunk loads. Nothing
                            without the provider `router.tsx` wraps the frame in. */}
                        <MailboxConnectBanner onGone={focusPageHeadingAfterBanner} />
                        <Suspense fallback={<RouteSkeleton />}>
                            <PageHeadingFocus pending={focusPageOnNavigate} mainRef={mainRef} />
                            <RouteTitle />
                            <Outlet />
                        </Suspense>
                    </main>
                </div>
            </div>
        </ComposeEmailProvider>
    );
}
